import React from 'react';
import { Flex, Button, useBreakpointValue } from '@skin-seoul/design-system';
import { ChevronIcon } from '../assets/icons/ChevronIcon';
import { ArrowIcon } from '../assets/icons/ArrowIcon';

import type { ComponentProps } from 'react';

type FlexProps = ComponentProps<typeof Flex>;
type ButtonProps = ComponentProps<typeof Button>;

const Container = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    position="relative"
    align="center"
    justify="center"
    w="full"
    p={{ base: '12px 0', tablet: '0' }}
    gap={{ base: '0', tablet: '8px' }}
    {...props}
  >
    {children}
  </Flex>
);

const PaginationNumberWrapper = ({
  children,
  ...props
}: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    align="center"
    bg={{ base: '#ffffff', tablet: 'none' }}
    border={{ base: '1px solid #f7f7f7', tablet: 'none' }}
    overflow="hidden"
    m={{ base: '0 -1px', tablet: '0' }}
    gap={{ base: '0', tablet: '2px' }}
    justify={{ base: 'center', tablet: 'center' }}
    borderRadius={{ base: '0', tablet: '0' }}
    {...props}
  >
    {children}
  </Flex>
);

const PaginationButton = ({
  children,
  isActive,
  onClick,
  disabled,
  ...props
}: {
  children: React.ReactNode;
  isActive?: boolean;
  onClick: () => void;
  disabled?: boolean;
} & Omit<ButtonProps, 'children' | 'onClick' | 'disabled'>) => (
  <Button
    w="40px"
    h="40px"
    border="none"
    bg={isActive ? { base: '#F9FAFB', tablet: '#F7F7F7' } : '#FFFFFF'}
    color={{ base: '#344054', tablet: '#303030' }}
    fontFamily="Plus Jakarta Sans"
    style={{
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRight: '1px solid #f7f7f7',
    }}
    cursor="pointer"
    borderRadius={{ base: '0', tablet: '9999px' }}
    p={{ base: '0', tablet: '10px' }}
    transition="all 0.2s"
    _hover={{ bg: !disabled ? '#f7f7f7' : undefined }}
    _disabled={{
      cursor: 'default',
      color: { base: '#344054', tablet: '#303030' },
    }}
    onClick={onClick}
    disabled={disabled}
    {...props}
    css={{
      '@media (min-width: 768px)': {
        borderRight: 'none',
      },
      '&:last-child': {
        borderRight: 'none',
      },
    }}
  >
    {children}
  </Button>
);

const ArrowButton = ({
  children,
  onClick,
  disabled,
  position: buttonPosition,
  ...props
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  position?: 'left' | 'right';
} & Omit<ButtonProps, 'children' | 'onClick' | 'disabled'>) => (
  <Button
    w={{ base: '42px', tablet: 'auto' }}
    h={{ base: '42px', tablet: '36px' }}
    border="1px solid #f7f7f7"
    bg="#ffffff"
    cursor="pointer"
    p={{ base: '0', tablet: '8px 12px' }}
    borderRadius={{ base: '0', tablet: '8px' }}
    color={{ base: 'inherit', tablet: '#344054' }}
    fontFamily={{ base: 'inherit', tablet: 'Plus Jakarta Sans' }}
    style={{
      fontSize: '14px',
      fontWeight: '400',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
    }}
    transition="all 0.2s"
    position={{ base: 'static', tablet: 'absolute' }}
    left={buttonPosition === 'left' ? { base: 'auto', tablet: '0' } : 'auto'}
    right={buttonPosition === 'right' ? { base: 'auto', tablet: '0' } : 'auto'}
    _hover={{ bg: !disabled ? '#f9fafb' : undefined }}
    _disabled={{
      cursor: 'default',
      opacity: { base: '1', tablet: '0.5' },
      borderColor: { base: 'inherit', tablet: '#c4c4c4' },
    }}
    onClick={onClick}
    disabled={disabled}
    {...props}
    css={{
      '& span': {
        display: 'none',
      },
      '@media (min-width: 768px)': {
        '& span': {
          display: 'inline',
        },
      },
    }}
  >
    {children}
  </Button>
);

type PageItem = number | '...';

interface ReviewPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const ReviewPagination = ({ currentPage, totalPages, onPageChange }: ReviewPaginationProps) => {
  const isPC = useBreakpointValue({ base: false, tablet: true });
  const Icon = isPC ? ChevronIcon : ArrowIcon;

  if (totalPages <= 1) {
    return null;
  }

  const renderPageNumbers = (): PageItem[] => {
    const pages: PageItem[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Container>
      <ArrowButton position="left" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        <Icon direction="left" disabled={currentPage === 1} />
        <span>Previous</span>
      </ArrowButton>

      <PaginationNumberWrapper>
        {renderPageNumbers().map((page, index) => (
          <PaginationButton
            key={index}
            isActive={page === currentPage}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={typeof page !== 'number'}
          >
            {page}
          </PaginationButton>
        ))}
      </PaginationNumberWrapper>

      <ArrowButton position="right" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        <span>Next</span>
        <Icon direction="right" disabled={currentPage === totalPages} />
      </ArrowButton>
    </Container>
  );
};

export default ReviewPagination;
